import { FC } from "react";
import { CheckBoxDays } from "../atoms/CheckBoxDays";
import { InputWithCornerHint } from "../atoms/InputWithCornerHint";
import { MultiSelectCategorys } from "../atoms/MultiSelectCategorys";
import { DateRangePicker } from "../atoms/DataRangePicker";
import { Select } from "./Select";
import { SelectAvatar } from "./SelectAvatar";
import { t } from "i18next";
import { FormikProps } from "formik";
import { MaintenanceValues } from "@/shared/schemas/maintenances.schema";
import { User } from "@/app/auth/auth";
import { DateTime } from "luxon";
import { urgencies } from "@/app/maintenance/maintenance";

export type FormMaintenanceProps = {
  formik: FormikProps<MaintenanceValues>;
  personal: Array<User>;
  isEditingMaintenance?: boolean;
  inventoryItems: Array<any>;
};

export const FormMaintenance: FC<FormMaintenanceProps> = ({
  formik,
  personal,
  inventoryItems = [],
  isEditingMaintenance,
}) => {
  // Extract selected inventory item IDs from items_inventory (if it exists in the maintenance data)
  const maintenanceData = formik.values as any;
  const selectedInventoryItemIds =
    maintenanceData.items_inventory?.map((item: any) => item.item_id) || [];

  // Initialize inventoryItemIds if it doesn't exist or is undefined
  if (!formik.values.inventoryItemIds) {
    formik.setFieldValue("inventoryItemIds", []);
  }

  // Get the selected inventory items with the correct structure for react-select
  const selectedInventoryItems = inventoryItems
    .filter((item) => selectedInventoryItemIds.includes(item.id))
    .map((item) => ({
      ...item,
      label: item.name,
      value: item.id,
    }));

  const inventoryItemGroups = Object.entries(
    Object.groupBy(inventoryItems, (inventoryItem) => {
      return inventoryItem?.name?.trim();
    })
  ).map(([inventoryName, inventoryItems]) => ({
    label: inventoryName,
    options: inventoryItems.map((inventoryItem) => ({
      ...inventoryItem,
      label: inventoryItem.name,
      value: inventoryItem.id,
    })),
  }));

  const handleInventoryItemsChange = (options: any[]) => {
    // Update inventoryItemIds for form validation - ensure it's always an array
    const itemIds = options.map((option) => option.id);
    const newItemsInventory = options.map((option) => ({
      id: option.id,
      item_id: option.id,
      maintenance_id: maintenanceData.id,
    }));

    // Update both values and mark as touched in the same operation
    formik.setFieldValue("inventoryItemIds", itemIds, true);
    formik.setFieldValue("items_inventory", newItemsInventory, true);
  };

  return (
    <form onSubmit={formik.handleSubmit}>
      <div className="mt-6 mx-2">
        <div className="space-y-12">
          <div className="grid grid-cols-1 gap-x-8 gap-y-10 pb-12 md:grid-cols-3">
            <div className="flex items-center justify-center h-full text-center">
              <div>
                <h2 className="text-base font-semibold leading-7 text-gray-900">
                  {t("maintenance.views.task")}
                </h2>
              </div>
            </div>
            <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("maintenance.views.task")}
                  span={t("general.mandatory")}
                  name="task"
                  type="text"
                  disabled={isEditingMaintenance}
                  placeholder={t("maintenance.views.task")}
                  onChange={formik.handleChange}
                  value={formik.values.task}
                  error={formik.touched.task && !!formik.errors.task}
                  textError={formik.errors.task}
                />
              </div>
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("maintenance.views.observation")}
                  span={t("general.mandatory")}
                  id="observation"
                  name="observation"
                  type="text"
                  placeholder={t("maintenance.views.observation")}
                  onChange={formik.handleChange}
                  value={formik.values.observation}
                  error={
                    formik.touched.observation && !!formik.errors.observation
                  }
                  textError={formik.errors.observation}
                />
              </div>
              {!isEditingMaintenance ? (
                <div className="col-span-full">
                  <label
                    htmlFor="days"
                    className="text-sm font-medium text-gray-700"
                  >
                    Intervalos
                  </label>
                  <CheckBoxDays
                    onChange={(days: string[]) =>
                      formik.setFieldValue("days", days)
                    }
                    value={formik.values.days}
                    error={formik.touched.days && !!formik.errors.days}
                  />
                </div>
              ) : null}

              {!isEditingMaintenance ? (
                <div className="col-span-full">
                  <DateRangePicker
                    span={t("general.optional")}
                    id="date"
                    label={t("lesson.views.interval_date")}
                    value={{
                      startDate: formik.values.startDate
                        ? DateTime.fromFormat(
                            formik.values.startDate,
                            "yyyy-MM-dd"
                          ).toJSDate()
                        : new Date(),
                      endDate: formik.values.endDate
                        ? DateTime.fromFormat(
                            formik.values.endDate,
                            "yyyy-MM-dd"
                          ).toJSDate()
                        : new Date(),
                    }}
                    onDateChange={(startDate, endDate) => {
                      formik.setFieldValue(
                        "startDate",
                        DateTime.fromJSDate(startDate).toFormat("yyyy-MM-dd")
                      );
                      formik.setFieldValue(
                        "endDate",
                        DateTime.fromJSDate(endDate).toFormat("yyyy-MM-dd")
                      );
                    }}
                  />
                </div>
              ) : null}

              {isEditingMaintenance ? (
                <div className="col-span-full">
                  <InputWithCornerHint
                    label={t("general.date")}
                    span={t("general.mandatory")}
                    name="date"
                    id="date"
                    error={formik.touched.date && formik.errors.date}
                    textError={formik.errors.date}
                    onChange={formik.handleChange}
                    value={formik.values.date}
                    type="date"
                    placeholder={t("pages.accounting.client")}
                  />
                </div>
              ) : null}

              <div className="col-span-full">
                <SelectAvatar
                  span={t("general.optional")}
                  name="assignedId"
                  selected={personal?.find(
                    (user: any) => user.id == formik?.values?.assignedId
                  )}
                  setSelected={(selected) =>
                    formik?.setFieldValue("assignedId", selected?.id)
                  }
                  options={personal}
                  tittle={t("maintenance.views.assigned_to")}
                  error={
                    formik.touched.assignedId && !!formik.errors.assignedId
                  }
                />
              </div>
              <div className="col-span-full">
                <Select
                  name="state"
                  span={t("general.mandatory")}
                  selected={urgencies.find(
                    (urgency) => urgency.id == formik.values.urgency
                  )}
                  setSelected={(urgency) =>
                    formik.setFieldValue("urgency", urgency.id)
                  }
                  options={urgencies}
                  tittle={t("maintenance.views.urgency")}
                  error={
                    formik?.errors.urgency &&
                    (formik?.touched.urgency as boolean)
                  }
                  textError={
                    Boolean(formik?.errors.urgency) &&
                    Boolean(formik?.touched.urgency)
                      ? (formik.errors.urgency as string)
                      : ""
                  }
                />
              </div>
              <div className="col-span-full">
                <MultiSelectCategorys
                  span={t("general.mandatory")}
                  error={
                    formik.touched.inventoryItemIds &&
                    formik.errors.inventoryItemIds
                      ? (formik.errors.inventoryItemIds as string)
                      : ""
                  }
                  value={selectedInventoryItems}
                  label={t("maintenance.views.instalations")}
                  options={inventoryItemGroups}
                  onChange={handleInventoryItemsChange}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </form>
  );
};

export default FormMaintenance;
